
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useForm } from "@/context/FormContext";
import { FormFieldType } from "@/types/form";
import FieldWrapper from "../FieldWrapper";
import { useStudentSearch } from "./useStudentSearch";
import { Student } from "./types";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface StudentSearchFieldProps {
  element: FormFieldType;
}

export const StudentSearchField: React.FC<StudentSearchFieldProps> = ({ element }) => {
  const { formData, setField } = useForm();
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const isMobile = useIsMobile();
  
  const { 
    searchQuery, 
    setSearchQuery, 
    students, 
    isLoading 
  } = useStudentSearch(true, () => {});

  // Check if student data is already filled
  useEffect(() => {
    // If student name is already set in formData, try to restore selected student state
    const studentName = formData["student_full_name"] as string;
    if (studentName && !selectedStudent) {
      const studentId = formData["student_id"] as string;
      const studentNickname = formData["student_nickname"] as string;
      
      if (studentName && studentNickname && studentId) {
        setSelectedStudent({
          id: studentId, // Using student_id as a fallback id
          full_name: studentName,
          nickname: studentNickname,
          student_id: studentId
        });
      }
    }
  }, [formData, selectedStudent]);

  // Handle student selection
  const handleSelectStudent = (option: any) => {
    if (!option) {
      handleClearSelection();
      return;
    }
    
    const student = option.student;
    setSelectedStudent(student);
    
    // Update the form data with student information
    setField("student_full_name", student.full_name);
    setField("student_nickname", student.nickname);
    setField("student_id", student.student_id);
    
    toast({
      title: "นักเรียนที่เลือก",
      description: `${student.full_name} (${student.nickname})`,
    });
  };

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedStudent(null);
    setSearchQuery("");
    setField("student_full_name", "");
    setField("student_nickname", "");
    setField("student_id", "");
  };

  // Convert students array to options format for react-select
  const options = students.map(student => ({
    value: student.id,
    label: `${student.full_name} (${student.nickname})${student.student_id ? ` - รหัส ${student.student_id}` : ''}`,
    student: student
  }));

  // Handle input change for search
  const handleInputChange = (inputValue: string) => {
    setSearchQuery(inputValue);
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '40px',
      borderRadius: '0.375rem',
      borderColor: '#e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#cbd5e1',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '0.375rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#e2e8f0' : undefined,
      color: state.isSelected ? 'white' : 'inherit',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#3b82f6',
      },
    }),
  };

  return (
    <FieldWrapper element={element}>
      <Select
        options={options}
        value={selectedStudent ? {
          value: selectedStudent.id,
          label: `${selectedStudent.full_name} (${selectedStudent.nickname})${selectedStudent.student_id ? ` - รหัส ${selectedStudent.student_id}` : ''}`,
          student: selectedStudent
        } : null}
        onChange={handleSelectStudent}
        onInputChange={handleInputChange}
        isLoading={isLoading}
        isClearable={true}
        placeholder="ค้นหาข้อมูลนักเรียน..."
        noOptionsMessage={() => searchQuery.length < 3 ? "พิมพ์อย่างน้อย 3 ตัวอักษรเพื่อค้นหา" : "ไม่พบข้อมูลนักเรียน"}
        styles={customStyles}
        className="react-select-container"
        classNamePrefix="react-select"
        menuPlacement={isMobile ? "auto" : "bottom"}
        menuPosition="fixed"
      />
      
      {!selectedStudent && (
        <p className="text-sm mt-1 text-orange-500">* ข้อมูลนักเรียนจะแสดงในช่องถัดไปหลังจากเลือกจากการค้นหาเท่านั้น</p>
      )}
    </FieldWrapper>
  );
};

export default StudentSearchField;
